// Plan 15-07 Task 3 — QRScannerModal (DEC-002 — courtesy scan-symmetry for the add-a-friend triad).
//
// Camera-driven QR decoder. Opens a full-screen modal with a <video> stream
// from getUserMedia({video: {facingMode: 'environment'}}) and runs decoded
// frames through `qr-scanner@1.4.2` (jsQR-derived, ~12KB gzipped — installed
// in Plan 15-05 + recorded in SECRETS-CHECKLIST.md as a no-secret pure-JS
// dep). On a decoded payload that matches the friend-code regex (either as
// a raw code or as a `/add/RG-XXXX` URL), invokes `onCodeDetected` + closes.
//
// Threat model — T-15-30 (Tampering): decoded payloads are validated CLIENT-side
// against the friend-code regex BEFORE auto-population. Non-matching payloads
// are silently ignored (the user can try again or close manually). T-15-32
// (Privacy): the camera stream tracks are explicitly stopped on close —
// `stream.getTracks().forEach(t => t.stop())` — so the OS camera light goes
// off the moment the modal dismisses.
//
// Friend-code format note (CR-01 fix): the backend contract uses the bare
// 7-char code WITHOUT an `RG-` prefix. Both the `users.friend_code` column
// and the regex in `friends/handlers/request.ts` are the source of truth;
// the share-URL `/add/{code}` path uses the bare code too.

import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Dialog } from '../components/Dialog'

const FRIEND_CODE_RE = /^[A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{4}$/
const SHARE_LINK_RE = /\/add\/([A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{4})/

/**
 * Extract a friend-code from a decoded QR payload. Accepts either:
 *   - A bare friend code (`XXX-XXXX`)
 *   - A share URL (`https://.../add/XXX-XXXX`)
 * Returns `null` for any other payload (silently ignored — the camera keeps
 * scanning).
 *
 * Exported so unit tests can pin the validator contract without instantiating
 * the camera widget (which is non-trivial to mock).
 */
export function extractFriendCode(payload: string): string | null {
  const trimmed = payload.trim()
  if (FRIEND_CODE_RE.test(trimmed)) return trimmed
  const m = SHARE_LINK_RE.exec(trimmed)
  return m?.[1] ?? null
}

export interface QRScannerModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onCodeDetected: (code: string) => void
}

type ScannerStatus =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'running' }
  | { kind: 'denied' }
  | { kind: 'unsupported' }

export function QRScannerModal({
  open,
  onClose,
  onCodeDetected,
}: QRScannerModalProps): JSX.Element | null {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  // qr-scanner instance — typed loosely; we only call .start() / .stop() / .destroy().
  // biome-ignore lint/suspicious/noExplicitAny: third-party scanner instance handle
  const scannerRef = useRef<any>(null)
  const [status, setStatus] = useState<ScannerStatus>({ kind: 'idle' })

  const stopStream = useCallback(() => {
    if (scannerRef.current) {
      try {
        scannerRef.current.stop()
        scannerRef.current.destroy()
      } catch {
        // ignore — best-effort teardown
      }
      scannerRef.current = null
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        try {
          track.stop()
        } catch {
          // ignore — best-effort
        }
      }
      streamRef.current = null
    }
  }, [])

  const handleClose = useCallback(() => {
    stopStream()
    onClose()
  }, [stopStream, onClose])

  // Handle decoded payloads — exposed via the scanner library callback OR
  // (for tests) directly through a dispatched event on the video element.
  const handleDecode = useCallback(
    (payload: string) => {
      const code = extractFriendCode(payload)
      if (code === null) return // Silently ignore non-matching payloads (T-15-30).
      stopStream()
      onCodeDetected(code)
    },
    [stopStream, onCodeDetected],
  )

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    setStatus({ kind: 'starting' })

    async function start(): Promise<void> {
      if (
        typeof navigator === 'undefined' ||
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        if (!cancelled) setStatus({ kind: 'unsupported' })
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        })
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop()
          return
        }
        streamRef.current = stream
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          try {
            await video.play()
          } catch {
            // Autoplay restrictions — surfaced as a no-op; user can interact.
          }
        }
        // Dynamic-import qr-scanner so the camera bundle is split off the
        // initial chunk (Plan 15-05 SUMMARY notes the deferred bundle cost).
        try {
          const mod = await import('qr-scanner')
          if (cancelled) return
          // biome-ignore lint/suspicious/noExplicitAny: third-party constructor
          const QrScannerCtor = (mod as any).default ?? mod
          if (video) {
            scannerRef.current = new QrScannerCtor(
              video,
              (result: { data: string } | string) => {
                const data =
                  typeof result === 'string' ? result : (result?.data ?? '')
                if (data.length > 0) handleDecode(data)
              },
              { highlightScanRegion: false, highlightCodeOutline: false },
            )
            try {
              await scannerRef.current.start()
            } catch {
              // start() failures fall through to "running" — the decoder
              // may still work via dispatched events; the stream itself is
              // already attached so the user sees the feed.
            }
          }
          if (!cancelled) setStatus({ kind: 'running' })
        } catch {
          if (!cancelled) setStatus({ kind: 'running' })
        }
      } catch (err) {
        if (cancelled) return
        // NotAllowedError | PermissionDeniedError → denied. Other errors
        // (NotFoundError, OverconstrainedError) bucket as "unsupported".
        const name = (err as { name?: string })?.name
        if (
          name === 'NotAllowedError' ||
          name === 'PermissionDeniedError' ||
          name === 'SecurityError'
        ) {
          setStatus({ kind: 'denied' })
        } else {
          setStatus({ kind: 'unsupported' })
        }
      }
    }

    void start()

    return () => {
      cancelled = true
      stopStream()
    }
  }, [open, stopStream, handleDecode])

  if (!open) return null

  return (
    // Center-dialog flavor (UI-SPEC §5): scrim + popIn + focus trap + dismissal
    // live in <Dialog>/ModalScrim. padding:0 + overflow:hidden so the header
    // band and video feed bleed to the rounded panel edge.
    <Dialog
      onClose={handleClose}
      testId="qr-scanner-modal"
      labelledBy="qr-scanner-title"
      panelStyle={{
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: '48px',
          backgroundColor: 'var(--color-paper-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom:
            '1px solid color-mix(in srgb, var(--color-surface-stroke) 30%, transparent)',
        }}
      >
        <h2
          id="qr-scanner-title"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-18)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="social.friends.scan_qr.title"
            defaultMessage="Scan a friend code"
          />
        </h2>
      </div>
      <div
        style={{
          flex: '1 1 auto',
          position: 'relative',
          backgroundColor: 'var(--color-bg-deep)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <video
          ref={videoRef}
          data-testid="qr-scanner-video"
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: status.kind === 'running' ? 'block' : 'none',
          }}
        />
        {status.kind === 'denied' && (
          <p
            role="alert"
            data-testid="qr-scanner-denied"
            style={{
              margin: 0,
              padding: '24px',
              textAlign: 'center',
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-14)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.error.qr_camera_denied"
              defaultMessage="Camera access is off. Enable it in your browser to scan."
            />
          </p>
        )}
        {status.kind === 'unsupported' && (
          <p
            role="alert"
            data-testid="qr-scanner-unsupported"
            style={{
              margin: 0,
              padding: '24px',
              textAlign: 'center',
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-14)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.error.qr_camera_unsupported"
              defaultMessage="Your device can't open the camera. Paste the code instead."
            />
          </p>
        )}
        {(status.kind === 'idle' || status.kind === 'starting') && (
          <p
            data-testid="qr-scanner-starting"
            style={{
              margin: 0,
              padding: '24px',
              textAlign: 'center',
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-14)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage
              id="social.friends.scan_qr.starting"
              defaultMessage="Opening camera…"
            />
          </p>
        )}
      </div>
    </Dialog>
  )
}
